import { Button, IconButton, Space, Text, useModal, withModalConditionalRender } from '@billy/design-system'
import { useLocalStorage } from '@rehooks/local-storage'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { LocalStorageKeys } from '../../../../enums/localStorageKeys'
import { ModalId } from '../../../../enums/modalId'
import { useSegment } from '../../../../hooks'
import { useShouldDisplayExternalInvoicesSyncInitiateAction } from '../../hooks/useShouldDisplayExternalInvoicesSyncInitiateAction'
import { ExternalInvoicesSyncModal as ExternalInvoicesSyncModalComponent } from '../ExternalInvoicesSyncModal'
import * as Styled from './styles'

const ExternalInvoicesSyncModal = withModalConditionalRender(ExternalInvoicesSyncModalComponent)

const externalInvoicesSyncModalId = ModalId.ExternalInvoicesSyncModal

export const ExternalInvoicesSyncDashboardBanner = (): ReactElement | null => {
  const { t } = useTranslation()
  const [isBannerClosed, setIsBannerClosed] = useLocalStorage<boolean>(
    LocalStorageKeys.DashboardExternalInvoicesSyncBanner,
    false,
  )
  const { track } = useSegment()
  const { shouldDisplayExternalInvoicesSyncInitiateAction } = useShouldDisplayExternalInvoicesSyncInitiateAction()
  const { open: openExternalInvoicesSyncModal } = useModal(externalInvoicesSyncModalId)

  const shouldDisplayBanner = useMemo(() => {
    return shouldDisplayExternalInvoicesSyncInitiateAction && !isBannerClosed
  }, [isBannerClosed, shouldDisplayExternalInvoicesSyncInitiateAction])

  const handleShowMeHowButtonClick = useCallback(() => {
    openExternalInvoicesSyncModal()
    track('xxx-sales-HistoricalSync-Dashboard Banner Clicked', {
      buttonName: 'Show Me How',
    })
  }, [openExternalInvoicesSyncModal, track])

  const handleCloseButtonClick = useCallback(() => {
    setIsBannerClosed(true)
    track('xxx-sales-HistoricalSync-Dashboard Banner Clicked', {
      buttonName: 'Close',
    })
  }, [setIsBannerClosed, track])

  useEffect(() => {
    if (shouldDisplayBanner) {
      track('xxx-sales-HistoricalSync-Dashboard Banner Displayed')
    }
  }, [shouldDisplayBanner, track])

  if (!shouldDisplayBanner) {
    return null
  }

  return (
    <>
      <Styled.ExternalInvoicesSyncBannerWrapper>
        <Text variant="h4">{t('dashboard.banner.external_invoices_sync.sync_your_invoice_history')}</Text>
        <Space />
        <Button iconRight="arrowRight" onClick={handleShowMeHowButtonClick} size="sm" variant="onColor" color="accent">
          {t('dashboard.banner.external_invoices_sync.show_me_how')}
        </Button>
        <Styled.CloseIconWrapper>
          <IconButton icon="x" onClick={handleCloseButtonClick} size="sm" variant="text" />
        </Styled.CloseIconWrapper>
      </Styled.ExternalInvoicesSyncBannerWrapper>
      <ExternalInvoicesSyncModal id={externalInvoicesSyncModalId} />
    </>
  )
}
